import '.a{display:flex;}.ssr-plugin'
import '.c{justify-content:center;}.ssr-plugin'
import '.h{width:100%;}.ssr-plugin'
import '.cz{margin-top:96px;}.ssr-plugin'
import '.d1{max-width:872px;}.ssr-plugin'
import '.d2{flex-grow:1;}.ssr-plugin'
import '.v{justify-content:space-between;}.ssr-plugin'
import '.b{align-items:center;}.ssr-plugin'
import '.i{flex-direction:column;}.ssr-plugin'
import '.j{gap:16px;}.ssr-plugin'
import '.hf{height:600px;}.ssr-plugin'
import '.d4{margin-bottom:16px;}.ssr-plugin'
import '.d3{flex-direction:row;}.ssr-plugin'
import { styled } from 'inlines'
import { Text } from '../Text/index.js'
import { useClient, useQuery } from '@based/sdk/react'
import { Table } from '../Table/index.js'
import { Loader } from '../Loader/index.js'
import { ScrollArea } from '../ScrollArea/index.js'
import { Avatar } from '../Avatar/index.js'
import { userAvatarColors } from '../UserMenu/index.js'
import { hash } from '@saulx/hash'
import { useEffect, useRef, useState } from 'react'
import { useOverlay } from '../../hooks/useOverlay.js'
import { Modal } from '../Modal/index.js'
import { Button } from '../Button/index.js'
import { TextInput } from '../TextInput/index.js'
import { SelectInput } from '../SelectInput/index.js'
import { useUpdate } from '../../hooks/useUpdate.js'
import { Sort } from '../SortInput/index.js'
import { Note } from '../Note/index.js'
import { Dialog } from '../Dialog/index.js'
import { Stack } from '../Stack/index.js'
import { useToast } from '../Toast/index.js'
import { Badge } from '../Badge/index.js'
import { colors } from '../../tokens/colors.js'
import { radius } from '../../utils/radius.js'

type Values = {
  id?: number
  name?: string
  email?: string
  role?: string
}
const validateValues = (values: Values) =>
  values.email?.length && values.role?.length

type WorkspaceSettingsProps = {
  roles: Record<string, string>
  rolesNotes?: Record<string, { title: string; description: string }>
  updateUserEndpoint?: string
  usersEndpoint?: string
}

export const WorkspaceSettings = ({
  roles = { Admin: 'admin', Viewer: 'viewer' },
  rolesNotes,
  updateUserEndpoint = 'ui-auth-update-user',
  usersEndpoint = 'ui-users2',
}: WorkspaceSettingsProps) => {
  const client = useClient()
  const toast = useToast()
  const token = client.authState?.token
  const [sort, setSort] = useState<Sort>()
  const update = useUpdate()

  const { data, loading } = useQuery(usersEndpoint, { token, sort })

  const [dialogValues, setDialogValues] = useState<Values>({})
  const [openManageUser, setOpenManageUser] = useState(false)
  const [openDeleteUser, setOpenDeleteUser] = useState(false)

  useEffect(() => {
    if (openManageUser) {
      showManageUser()
    }
  }, [openManageUser])

  useEffect(() => {
    if (openDeleteUser) {
      showDeleteUser()
    }
  }, [openDeleteUser])

  const showManageUser = useOverlay(Modal, {
    size: 'small',
    onClose: () => {
      setOpenManageUser(false)
      setDialogValues({})
    },
    children: () => {
      const [internalState, setInternalState] = useState(dialogValues)
      return (
        <Modal.Content>
          <Modal.Header title={`Manage ${dialogValues.name?.split(' ')[0]}`} />
          <Modal.Body>
            <TextInput
              value={dialogValues.email}
              onChange={() => {}}
              disabled
              label="Email"
            />
            <SelectInput
              value={internalState.role}
              onChange={(value) => {
                if (typeof value === 'string') {
                  const newValues = {
                    ...dialogValues,
                    role: value,
                  }
                  setInternalState(newValues)
                  setDialogValues(newValues)
                  update()
                }
              }}
              options={Object.entries(roles).map(([key, value]) => ({
                label: key,
                value,
              }))}
              label="Role"
            />
            {rolesNotes?.[internalState?.role] ? (
              <Note
                title={rolesNotes[internalState.role].title}
                description={rolesNotes[internalState.role].description}
              />
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="border"
              onClick={() => {
                Modal.Close()
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={!validateValues(internalState)}
              onClick={async () => {
                await client.call(updateUserEndpoint, {
                  userId: internalState.id,
                  role: internalState.role,
                })
                Modal.Close()
              }}
            >
              Update
            </Button>
          </Modal.Footer>
        </Modal.Content>
      )
    },
  })

  const showDeleteUser = useOverlay(Dialog, {
    onClose: () => {
      setOpenDeleteUser(false)
      setDialogValues({})
    },
    title: `Remove ${dialogValues.name?.split(' ')[0]} from the workspace?`,
    description: `${dialogValues.name?.split(' ')[0]} will not be able to access or edit the workspace anymore.`,
    children: () => {
      return (
        <Dialog.Content>
          <Stack gap={12}>
            <Button
              width="full"
              keyHint="Esc"
              variant="border"
              align="center"
              onClick={() => {
                Dialog.Close()
              }}
            >
              Cancel
            </Button>
            <Button
              width="full"
              keyHint="Enter"
              align="center"
              color="red"
              onClick={async () => {
                await client.call(updateUserEndpoint, {
                  userId: dialogValues.id,
                  remove: true,
                })
                Modal.Close()
              }}
            >
              Remove
            </Button>
          </Stack>
        </Dialog.Content>
      )
    },
  })

  const schema = {
    types: {
      user: {
        props: {
          id: {
            hide: true,
          },
          name: {
            type: 'string',
            // width: 330,
            render: (value, node) => {
              return (
                <Stack direction="row" gap={6} style={{ marginLeft: 8 }}>
                  <Avatar
                    src={node?.picture}
                    label={value}
                    size="small"
                    color={
                      userAvatarColors[
                        hash(node.email + value) % userAvatarColors.length
                      ]
                    }
                  />
                  <Text>{value}</Text>
                </Stack>
              )
            },
          },
          email: {
            type: 'alias',
            format: 'email',
            // width: 330,
          },
          picture: {
            type: 'string',
            mime: 'image/*',
            hide: true,
          },
          role: {
            enum: ['admin', 'viewer'],
            // width: 120,
            render: (value, node) => {
              return (
                <Badge
                  color={
                    node.status === 'pending' ? 'orange-fill' : 'neutral-subtle'
                  }
                >
                  {String(value).charAt(0).toUpperCase() +
                    String(value).slice(1) +
                    (node.status === 'pending' ? ' (pending)' : '')}
                </Badge>
              )
            },
          },
        },
      },
    },
  }
  return loading ? (
    <Loader />
  ) : (
    <ScrollArea>
      <div className="a c h cz"
      >
        <div className="d1 a d2 v b"
        >
          <div className="a i h j hf"
          >
            <Text variant="heading-medium" color="neutral100">
              Workspace settings
            </Text>
            <Text variant="display-regular" color="neutral60">
              Manage the members and settings of the workspace
            </Text>
            <div className="cz d4 a v d3 b"
            >
              <Text variant="subheading-medium" color="neutral100">
                Members & roles
              </Text>
              <Modal
                size="small"
                onClose={() => {
                  setDialogValues({})
                }}
              >
                <Modal.Trigger>
                  <Button variant="border" leadIcon="user-new">
                    Invite member
                  </Button>
                </Modal.Trigger>
                <Modal.Content>
                  <Modal.Header title="Invite new member" />
                  <Modal.Body>
                    <TextInput
                      value={dialogValues?.email}
                      onChange={(value) => {
                        setDialogValues({
                          ...dialogValues,
                          email: value,
                        })
                      }}
                      placeholder="Add email"
                      label="Email"
                    />
                    <SelectInput
                      value={dialogValues?.role}
                      placeholder="Pick option"
                      onChange={(value) => {
                        if (typeof value === 'string') {
                          const newValues = {
                            ...dialogValues,
                            role: value,
                          }
                          setDialogValues(newValues)
                          update()
                        }
                      }}
                      options={Object.entries(roles).map(([key, value]) => ({
                        label: key,
                        value,
                      }))}
                      label="Role"
                    />
                    {rolesNotes?.[dialogValues?.role] ? (
                      <Note
                        title={rolesNotes[dialogValues.role].title}
                        description={rolesNotes[dialogValues.role].description}
                      />
                    ) : null}
                  </Modal.Body>
                  <Modal.Footer>
                    <Button
                      variant="border"
                      onClick={() => {
                        Modal.Close()
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      disabled={!validateValues(dialogValues)}
                      onClick={async () => {
                        const result = await client.call('ui-invite-user', {
                          email: dialogValues.email,
                          role: dialogValues.role,
                        })
                        if (result?.ok) {
                          toast(`${dialogValues.email} invited`, {
                            description: `Invitation to ${dialogValues.role} role sent`,
                            icon: 'checkmark-filled',
                          })
                        } else if (result.error) {
                          toast(`${dialogValues.email} invited`, {
                            description: result.error,
                            icon: 'error',
                            color: 'red',
                          })
                        }
                        setDialogValues({})
                        Modal.Close()
                      }}
                    >
                      Send invite
                    </Button>
                  </Modal.Footer>
                </Modal.Content>
              </Modal>
            </div>
            <Stack
              grow
              style={{
                width: '100%',
                height: '100%',
                background: `linear-gradient(${colors.white5}),
                  linear-gradient(${colors.neutralInverted100})`,
                boxShadow: `0px 8px 12px ${colors.black10}`,
                borderRadius: radius[20],
                border: `1px solid &{colors.neutral10}`,
              }}
            >
              <Table
                data={data}
                colVisibility="fit"
                schema={schema as any}
                schemaType="user"
                outerBorder
                checkboxVisibility="never"
                actionsVisibility="hover"
                pagination={{
                  type: 'scroll',
                  total: data?.length,
                }}
                onSortChange={setSort}
                rowActions={{
                  manage: {
                    leadIcon: 'user-setting',
                    label: 'Manage',
                    onClick: (_index, values) => {
                      setDialogValues(values)
                      setOpenManageUser(true)
                    },
                  },
                  remove: {
                    leadIcon: 'delete',
                    label: 'Remove',
                    color: 'red',
                    onClick: (index, values) => {
                      setDialogValues(values)
                      setOpenDeleteUser(true)
                    },
                  },
                }}
              />
            </Stack>
          </div>
        </div>
      </div>
    </ScrollArea>
  )
}
